import { useCallback, useContext, useEffect, useState } from "react";
import PageToolbar from "~/common/components/PageToolbar";
import OrderTotals from "~/features/cart/cart/components/OrderTotals";
import Spacer from "~/common/components/Spacer";
import ProductTable from "~/features/cart/activeQuote/components/productTable/ProductTable";
import FullscreenLoader from "~/features/loading/FullscreenLoader";
import classNames from "classnames";
import QuoteOrderTotals from "~/features/cart/activeQuote/components/ActiveQuoteEdit/QuoteOrderTotals";
import ToReviewButton from "~/features/cart/activeQuote/components/ActiveQuoteEdit/ToReviewButton";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import OptimeInfoPopup from "~/features/cart/cart/components/OptimeInfoPopup";
import ToastMessage from "~/common/components/Messages/ToastMessage/ToastMessage";
import { ActiveQuotePageModeContext } from "~/features/cart/activeQuote/components/ActiveQuotePage/ActiveQuotePageModeContext";
import { scrollToTop } from "~/common/utils/misc";
import { Mode } from "~/features/cart/cart/models/QuoteData";
import { sendInfoWanrSeenQuote } from "~/features/cart/activeQuote/api/trackDstError";
import { useTranslation } from "react-i18next";
import { DownloadReportButton } from "~/features/cart/cart/components/Buttons/DownloadReportButton/DownloadReportButton";
import { ReportType } from "~/features/cart/cart/components/Buttons/DownloadReportButton/api/downloadCartReport";
import { Button, Stack, Typography } from "~/common/mui";
import { type ActiveQuoteViewModel } from "~/features/cart/activeQuote/hooks/useActiveQuoteViewModel";
import { ActiveQuoteShowInternalContext } from "~/features/cart/activeQuote/components/productTable/ActiveQuoteShowInternalContext";

interface Props {
  viewModel: ActiveQuoteViewModel;
}

const ActiveQuoteEdit = (props: Props) => {
  const { viewModel } = props;
  const {
    totalWeight,
    totalSum,
    totalTargetSum,
    validCart,
    entryIds,
    requestInFlight,
    dstError,
    showOptimePopupOnCheckout,
    isPnaLoading,
  } = viewModel.aggregates;
  const deleteAll = viewModel.handleDeleteAll;
  const [showOptimeInfoPopup, setShowOptimeInfoPopup] = useState<boolean>(false);
  const [showDstError, setShowDstError] = useState<boolean>(false);

  const { t } = useTranslation();

  useEffect(() => {
    setShowDstError(dstError);
    if (dstError) {
      sendInfoWanrSeenQuote();
    }
  }, [dstError]);

  const isEmpty = entryIds.length === 0;

  const { val: showInternal, toggle: toggleShowInternal } = useBooleanState(false);

  // Go to review
  const setMode = useContext(ActiveQuotePageModeContext);
  const cbGoToReview = useCallback(() => {
    setMode(Mode.REVIEW);
    scrollToTop();
  }, [setMode]);

  const ToReviewClickHandler = () => {
    if (showOptimePopupOnCheckout) {
      setShowOptimeInfoPopup(true);
    } else {
      cbGoToReview();
    }
  };
  //

  const createQuoteButton = <ToReviewButton disabled={!validCart} clickHandler={ToReviewClickHandler} />;

  const disableReportDownload = isPnaLoading;

  return (
    <ActiveQuoteShowInternalContext.Provider value={showInternal}>
      {!isEmpty && (
        <PageToolbar
          left={
            <Stack gap={2} direction={"row"} className={"scha__active-quote__report-buttons"}>
              <Typography component="span" variant="labelLarge" color="text.secondary">
                {t("cartPage.downloads.title")}
              </Typography>
              <DownloadReportButton type={ReportType.CSV} disabled={disableReportDownload} />
              <DownloadReportButton type={ReportType.XLSX} disabled={disableReportDownload} />
            </Stack>
          }
          right={createQuoteButton}
        />
      )}

      <div className={classNames("scha__active-quote", showInternal && "scha__active-quote--show-details")}>
        <ProductTable
          tableViewModel={viewModel.editTableViewModel}
          data-test-id={"active-quote-section-regular"}
          onClearCart={deleteAll}
          onToggleDetails={toggleShowInternal}
          showInternal={showInternal}
        />

        {!isEmpty && (
          <>
            <Spacer isThick={true} className={"mb-30 mt-60 p-relative"} />
            {/*<QuoteThickSpacer className={'pb-30 pt-60'} showInternal={showInternal}/>*/}
            <div className={classNames("d-flex flex-justify-end", !showInternal && "row")}>
              <div className="col-4">
                <OrderTotals
                  showPriceOnRequest={false}
                  weight={totalWeight}
                  totalValue={totalSum}
                  hideTaxesMsg={true}
                />
                <Spacer className={"mt-3 mb-3"} />
              </div>

              {showInternal && (
                <>
                  <div className={"scha__active-quote__total-target-price px-3"}>
                    <div className="scha__active-quote__total-target-price__bg" />
                    <QuoteOrderTotals className={"p-relative"} totalValue={totalTargetSum} />
                    <Spacer className={"mt-3 mb-3 p-relative"} />
                  </div>
                  <div className={"scha__active-quote__btn-col-spacer"} />
                </>
              )}
            </div>

            <div className="d-flex flex-justify-end mt-30">{createQuoteButton}</div>
          </>
        )}
      </div>

      {requestInFlight && <FullscreenLoader />}
      {showOptimeInfoPopup && (
        <OptimeInfoPopup
          onClose={() => setShowOptimeInfoPopup(false)}
          closeButton={
            <Button variant={"text"} onClick={() => setShowOptimeInfoPopup(false)}>
              {t("activeQuotePage.backToQuote")}
            </Button>
          }
          continueButton={<Button onClick={cbGoToReview}>{t("activeQuotePage.send")}</Button>}
        />
      )}
      {showDstError && (
        <ToastMessage type={"warning"} onClose={() => setShowDstError(false)}>
          {t("quotePage.warnMsg.dst")}
        </ToastMessage>
      )}
    </ActiveQuoteShowInternalContext.Provider>
  );
};

export default ActiveQuoteEdit;
